/**
 * Path Remapping Helpers - Production Ready
 * 
 * Framework-agnostic utility for handling path remapping from TagadaPay routing
 * Includes error handling, SSR compatibility, performance optimizations, and edge case handling
 * 
 * @packageDocumentation
 */

import { match } from 'path-to-regexp';

// ============================================================================
// Types
// ============================================================================

/**
 * Path information including both external and internal paths
 */
export interface PathInfo {
  /** The path shown in the browser URL bar (external/remapped path) */
  externalPath: string;
  /** The path that the plugin expects (internal/original path) */
  internalPath: string | null;
  /** Whether path remapping is active */
  isRemapped: boolean;
  /** The full URL including protocol, host, query, and hash */
  fullUrl: string;
  /** Query parameters from the URL */
  query: URLSearchParams;
  /** URL hash without the # symbol */
  hash: string;
}

/**
 * Result of route matching with extracted parameters
 */
export interface RouteMatchResult {
  /** Whether the route matched */
  matched: boolean;
  /** Extracted URL parameters (empty object if no match or no params) */
  params: Record<string, string>;
}

// ============================================================================
// Private Cache
// ============================================================================

/**
 * Cache for internal path lookup to avoid repeated DOM queries
 * Cleared on navigation events
 */
let internalPathCache: string | null | undefined = undefined;

/**
 * Flag to check if we're in a browser environment
 * Prevents errors during SSR
 */
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Store original history methods for cleanup
 */
const originalPushState = isBrowser ? history.pushState.bind(history) : null;
const originalReplaceState = isBrowser ? history.replaceState.bind(history) : null;

/**
 * Clear the internal path cache
 * Called automatically on navigation events
 */
function clearCache(): void {
  internalPathCache = undefined;
}

// Setup cache invalidation on navigation (only in browser)
if (isBrowser) {
  // Clear cache on history navigation
  window.addEventListener('popstate', clearCache);

  // Clear cache on hash change
  window.addEventListener('hashchange', clearCache);

  // Intercept pushState and replaceState to clear cache
  history.pushState = function (...args) {
    clearCache();
    return originalPushState!.apply(this, args);
  };

  history.replaceState = function (...args) {
    clearCache();
    return originalReplaceState!.apply(this, args);
  };
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Get the internal path that the plugin expects
 * 
 * Reads from the meta tag injected by TagadaPay middleware.
 * Results are cached for performance.
 * 
 * **Development/Testing Mode (localhost only):**
 * You can simulate path remapping in two ways:
 * 1. Query parameter: `?__remap=/internal-path`
 * 2. localStorage with explicit external → internal mapping
 * 
 * @returns The internal path from meta tag, or null if no remapping is active
 * @throws {Error} If called in a non-browser environment (SSR)
 * 
 * @example
 * ```typescript
 * const internalPath = getInternalPath();
 * if (internalPath) {
 *   console.log('Remapped from external to:', internalPath);
 * }
 * ```
 * 
 * @example Simulating remapping in localhost - RECOMMENDED FORMAT
 * ```typescript
 * // NEW: Explicit mapping (clear which path remaps to which)
 * localStorage.setItem('tagadapay-remap', JSON.stringify({
 *   externalPath: '/myremap/:param',
 *   internalPath: '/hello-with-param/:myparam'
 * }));
 * // Then navigate to: http://localhost:5173/myremap/test123
 * // Result: Matches external pattern, uses internal path /hello-with-param/:myparam
 * 
 * // Option 2: Query parameter (for quick tests)
 * // Navigate to: http://localhost:5173/custom-page?__remap=/hello
 * 
 * // Clear simulation
 * localStorage.removeItem('tagadapay-remap');
 * ```
 * 
 * @example More Examples
 * ```typescript
 * // Remap /products/:id to /hello-with-param/:myparam
 * localStorage.setItem('tagadapay-remap', JSON.stringify({
 *   externalPath: '/products/:id',
 *   internalPath: '/hello-with-param/:myparam'
 * }));
 * // Visit: http://localhost:5173/products/abc123
 * 
 * // Remap /custom-hello to /hello
 * localStorage.setItem('tagadapay-remap', JSON.stringify({
 *   externalPath: '/custom-hello',
 *   internalPath: '/hello'
 * }));
 * // Visit: http://localhost:5173/custom-hello
 * ```
 */
export function getInternalPath(): string | null {
  // SSR check
  if (!isBrowser) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn('[TagadaPay SDK] getInternalPath() called in SSR context');
    }
    return null;
  }

  // Return cached value if available
  if (internalPathCache !== undefined) {
    console.log('[TagadaPay SDK] 📦 Returning cached internal path:', internalPathCache);
    return internalPathCache;
  }

  console.log('[TagadaPay SDK] 🔍 getInternalPath() called for:', window.location.pathname);

  try {
    // Development/Testing: Check for simulation via query parameter or localStorage
    // Only works on localhost for security
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.localhost');

    console.log('[TagadaPay SDK] 🏠 isLocalhost:', isLocalhost);

    if (isLocalhost) {
      // Priority 1: Query parameter ?__remap=/internal-path
      const urlParams = new URLSearchParams(window.location.search);
      const queryRemap = urlParams.get('__remap');
      console.log('[TagadaPay SDK] 🔍 Query parameter __remap:', queryRemap);

      if (queryRemap) {
        internalPathCache = queryRemap;
        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
          console.log(`[TagadaPay SDK] 🔧 Simulating path remap via query: ${window.location.pathname} → ${queryRemap}`);
        }
        return queryRemap;
      }

      // Priority 2: localStorage tagadapay-remap
      // Format: JSON object with external -> internal mapping
      // Example: {"externalPath": "/myremap/:param", "internalPath": "/hello-with-param/:myparam"}
      try {
        const storageRemap = localStorage.getItem('tagadapay-remap');
        console.log('[TagadaPay SDK] 🔍 localStorage tagadapay-remap:', storageRemap);

        if (storageRemap) {
          try {
            // Try parsing as JSON first (new explicit format)
            const remapConfig = JSON.parse(storageRemap);
            console.log('[TagadaPay SDK] 📝 Parsed remap config:', remapConfig);

            if (remapConfig.externalPath && remapConfig.internalPath) {
              const currentPath = window.location.pathname;
              console.log('[TagadaPay SDK] 🔍 Checking if', currentPath, 'matches pattern', remapConfig.externalPath);

              // Check if current path matches the external path pattern
              const matchFn = match(remapConfig.externalPath, {
                decode: decodeURIComponent,
                end: true
              });
              const result = matchFn(currentPath);
              console.log('[TagadaPay SDK] 🎯 Match result:', result);

              if (result !== false) {
                // Current path matches the external pattern, return internal path
                internalPathCache = remapConfig.internalPath;
                console.log(`[TagadaPay SDK] ✅ Path remap: ${remapConfig.externalPath} → ${remapConfig.internalPath}`);
                console.log(`[TagadaPay SDK] ✅ Current URL ${currentPath} matches external pattern`);
                console.log('[TagadaPay SDK] 📦 Caching and returning:', remapConfig.internalPath);
                return remapConfig.internalPath;
              } else {
                // Current path doesn't match the external pattern, no remap
                console.log(`[TagadaPay SDK] ❌ Current URL ${currentPath} does NOT match external pattern ${remapConfig.externalPath}`);
                internalPathCache = null;
                return null;
              }
            } else {
              console.log('[TagadaPay SDK] ⚠️ Parsed config missing externalPath or internalPath');
            }
          } catch (jsonError) {
            // Not JSON, treat as legacy format (just internal path)
            console.log('[TagadaPay SDK] ⚠️ Failed to parse as JSON, using legacy format:', jsonError);
            internalPathCache = storageRemap;
            if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
              console.log(`[TagadaPay SDK] 🔧 Simulating path remap (legacy): ${window.location.pathname} → ${storageRemap}`);
            }
            return storageRemap;
          }
        } else {
          console.log('[TagadaPay SDK] 📭 No localStorage tagadapay-remap found');
        }
      } catch (e) {
        console.log('[TagadaPay SDK] ❌ localStorage error:', e);
      }
    }

    // Production: Try to get internal path from full remap config first (new way)
    const fullRemapMetaTag = document.querySelector('meta[name="tagadapay-path-remap"]');
    if (fullRemapMetaTag) {
      try {
        const content = fullRemapMetaTag.getAttribute('content');
        if (content) {
          const decodedContent = decodeURIComponent(content);
          const parsed = JSON.parse(decodedContent);
          if (parsed.internal) {
            console.log('[TagadaPay SDK] ✅ Got internal path from full remap config:', parsed.internal);
            internalPathCache = parsed.internal;
            return parsed.internal;
          }
        }
      } catch (e) {
        console.error('[TagadaPay SDK] Error parsing full remap config:', e);
        // Fall through to try legacy meta tag
      }
    }

    // Fallback: Try legacy meta tag (old way, for backward compatibility)
    const metaTag = document.querySelector('meta[name="x-plugin-internal-path"]');

    if (!metaTag) {
      console.log('[TagadaPay SDK] ℹ️  No path remap meta tags found');
      internalPathCache = null;
      return null;
    }

    // Get and validate the content
    const content = metaTag.getAttribute('content');

    if (!content || content.trim() === '') {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.warn('[TagadaPay SDK] Meta tag found but content is empty');
      }
      internalPathCache = null;
      return null;
    }

    // Validate path format
    const trimmedContent = content.trim();
    if (!trimmedContent.startsWith('/')) {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.warn(`[TagadaPay SDK] Invalid internal path format: "${trimmedContent}" (must start with /)`);
      }
      internalPathCache = null;
      return null;
    }

    console.log('[TagadaPay SDK] ✅ Got internal path from legacy meta tag:', trimmedContent);
    // Cache and return
    internalPathCache = trimmedContent;
    return trimmedContent;
  } catch (error) {
    // Log error in development
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[TagadaPay SDK] Error reading internal path:', error);
    }

    internalPathCache = null;
    return null;
  }
}

/**
 * Check if path remapping is currently active
 * 
 * @returns true if a remapped path is configured, false otherwise
 * 
 * @example
 * ```typescript
 * if (isPathRemapped()) {
 *   console.log('User is viewing a remapped URL');
 * }
 * ```
 */
export function isPathRemapped(): boolean {
  return getInternalPath() !== null;
}

/**
 * Get comprehensive path mapping information
 * 
 * Returns both the external (browser) path and internal (plugin) path,
 * along with query parameters and hash.
 * 
 * @returns {PathInfo} Complete path information object
 * @throws {Error} If called in a non-browser environment (SSR)
 * 
 * @example
 * ```typescript
 * const pathInfo = getPathInfo();
 * console.log('Browser shows:', pathInfo.externalPath);
 * console.log('Plugin expects:', pathInfo.internalPath);
 * console.log('Query params:', pathInfo.query.get('id'));
 * ```
 */
export function getPathInfo(): PathInfo {
  // SSR check
  if (!isBrowser) {
    throw new Error('[TagadaPay SDK] getPathInfo() cannot be called in SSR context');
  }

  try {
    const externalPath = window.location.pathname;
    const internalPath = getInternalPath();
    const query = new URLSearchParams(window.location.search);
    const hash = window.location.hash.replace(/^#/, '');

    return {
      externalPath,
      internalPath,
      isRemapped: internalPath !== null,
      fullUrl: window.location.href,
      query,
      hash,
    };
  } catch (error) {
    // Fallback to safe defaults
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[TagadaPay SDK] Error getting path info:', error);
    }

    return {
      externalPath: '/',
      internalPath: null,
      isRemapped: false,
      fullUrl: '',
      query: new URLSearchParams(),
      hash: '',
    };
  }
}

/**
 * Check if the current URL should match a given internal path
 * 
 * Handles path remapping automatically. Use this for catch-all routing
 * implementations where you need to check multiple paths.
 * 
 * @param internalPath - The plugin's defined path (must start with /)
 * @returns true if current URL should render this path's component
 * @throws {Error} If internalPath is invalid
 * 
 * @example
 * ```typescript
 * // In a catch-all route handler
 * if (shouldMatchRoute('/hello')) {
 *   return <HelloPage />;
 * }
 * if (shouldMatchRoute('/about')) {
 *   return <AboutPage />;
 * }
 * ```
 */
/**
 * Helper function to match a path against a pattern using path-to-regexp
 * @param pathname - The actual path to test
 * @param pattern - The pattern to match against (can include :params, wildcards, etc.)
 * @returns Object with matched status and extracted params
 */
function matchesPathPattern(pathname: string, pattern: string): RouteMatchResult {
  try {
    // Exact match (no pattern)
    if (pathname === pattern) {
      return { matched: true, params: {} };
    }

    // Check if pattern contains special characters that require path-to-regexp
    // :param, *wildcard, {optional}, etc.
    const hasPatternSyntax = pattern.includes(':') || pattern.includes('*') || pattern.includes('{');

    if (!hasPatternSyntax) {
      // No pattern syntax - use exact match only
      // For paths without parameters, we should match exactly, not with prefix
      return { matched: pathname === pattern, params: {} };
    }

    // Use path-to-regexp for patterns with parameters, wildcards, or optional segments
    const matchFn = match(pattern, {
      decode: decodeURIComponent,
      end: true
    });
    const result = matchFn(pathname);

    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.log(`[TagadaPay SDK] Pattern match: "${pathname}" against "${pattern}" =`, result !== false);
    }

    if (result === false) {
      return { matched: false, params: {} };
    }

    // Extract params from result
    const params: Record<string, string> = {};
    if (typeof result === 'object' && result.params) {
      Object.keys(result.params).forEach(key => {
        const value = result.params[key];
        // Convert to string and handle arrays (for splat params)
        params[key] = Array.isArray(value) ? value.join('/') : String(value);
      });
    }

    return { matched: true, params };
  } catch (error) {
    // Fallback to exact match if path-to-regexp fails
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn(`[TagadaPay SDK] Pattern matching failed for "${pattern}":`, error);
    }
    return { matched: pathname === pattern, params: {} };
  }
}

/**
 * Map parameter names from external pattern to internal pattern
 * Assumes parameters appear in the same order in both patterns
 * 
 * @param externalParams - Parameters extracted from external URL
 * @param externalPattern - External path pattern (e.g., /off1/:lolo)
 * @param internalPattern - Internal path pattern (e.g., /post/:orderId)
 * @returns Mapped parameters with internal param names
 * 
 * @example
 * ```typescript
 * mapParamNames(
 *   {lolo: 'order_123'},
 *   '/off1/:lolo',
 *   '/post/:orderId'
 * )
 * // Returns: {orderId: 'order_123'}
 * ```
 */
function mapParamNames(
  externalParams: Record<string, string>,
  externalPattern: string,
  internalPattern: string
): Record<string, string> {
  // Extract parameter names from patterns
  const externalParamNames = extractParamNames(externalPattern);
  const internalParamNames = extractParamNames(internalPattern);

  // If no params or count mismatch, return original params
  if (externalParamNames.length === 0 ||
    externalParamNames.length !== internalParamNames.length) {
    return externalParams;
  }

  // Map parameters by position
  const mappedParams: Record<string, string> = {};
  externalParamNames.forEach((externalName, index) => {
    const internalName = internalParamNames[index];
    const value = externalParams[externalName];
    if (value !== undefined) {
      mappedParams[internalName] = value;
    }
  });

  return mappedParams;
}

/**
 * Extract parameter names from a path pattern
 * 
 * @param pattern - Path pattern (e.g., /post/:orderId/item/:itemId)
 * @returns Array of parameter names (e.g., ['orderId', 'itemId'])
 */
function extractParamNames(pattern: string): string[] {
  const matches = pattern.match(/:([a-zA-Z0-9_]+)/g);
  if (!matches) return [];
  return matches.map(m => m.substring(1)); // Remove ':' prefix
}

/**
 * Match a route and extract URL parameters
 * 
 * Handles path remapping automatically and extracts parameters from the URL.
 * This is the preferred method when you need both match status and parameters.
 * 
 * @param internalPath - The plugin's defined path (must start with /)
 * @returns Object with `matched` boolean and `params` object
 * @throws {Error} If internalPath is invalid
 * 
 * @example
 * ```typescript
 * // In a catch-all route handler
 * const result = matchRoute('/hello-with-param/:myparam');
 * if (result.matched) {
 *   console.log('Matched! Param:', result.params.myparam);
 *   return <HelloPage myparam={result.params.myparam} />;
 * }
 * ```
 */
export function matchRoute(internalPath: string): RouteMatchResult {
  // Validate input
  if (typeof internalPath !== 'string') {
    throw new Error(`[TagadaPay SDK] internalPath must be a string, got ${typeof internalPath}`);
  }

  if (internalPath.trim() === '') {
    throw new Error('[TagadaPay SDK] internalPath cannot be empty');
  }

  if (!internalPath.startsWith('/')) {
    throw new Error(`[TagadaPay SDK] internalPath must start with /, got "${internalPath}"`);
  }

  // SSR check
  if (!isBrowser) {
    return { matched: false, params: {} };
  }

  try {
    const currentPath = window.location.pathname;
    const remappedInternalPath = getInternalPath();

    // If remapped, need to extract params from external URL using external pattern
    if (remappedInternalPath) {
      const internalMatch = matchesPathPattern(remappedInternalPath, internalPath);
      if (!internalMatch.matched) {
        return { matched: false, params: {} };
      }

      // Get the external pattern from remap config
      let externalPattern: string | null = null;

      // Check localStorage for remap config (development/testing)
      if (typeof localStorage !== 'undefined') {
        try {
          const remapData = localStorage.getItem('tagadapay-remap');
          if (remapData) {
            const parsed = JSON.parse(remapData);
            if (parsed.externalPath && parsed.internalPath === remappedInternalPath) {
              externalPattern = parsed.externalPath;
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      // Check meta tag for production remap config
      if (!externalPattern && typeof document !== 'undefined') {
        console.log('[TagadaPay SDK] 🔍 Checking for production path remap meta tag...');
        const metaTag = document.querySelector('meta[name="tagadapay-path-remap"]');
        if (metaTag) {
          try {
            const content = metaTag.getAttribute('content');
            console.log('[TagadaPay SDK] 📄 Found meta tag content (encoded):', content?.substring(0, 100) + '...');
            if (content) {
              // Decode URL-encoded content before parsing
              const decodedContent = decodeURIComponent(content);
              console.log('[TagadaPay SDK] 📄 Decoded content:', decodedContent);
              const parsed = JSON.parse(decodedContent);
              console.log('[TagadaPay SDK] 📦 Parsed remap config:', parsed);
              console.log('[TagadaPay SDK] 🔍 Checking if internal path matches:', {
                parsedInternal: parsed.internal,
                remappedInternalPath
              });
              if (parsed.external && parsed.internal === remappedInternalPath) {
                externalPattern = parsed.external;
                console.log('[TagadaPay SDK] ✅ Using external pattern from meta tag:', externalPattern);
              } else {
                console.log('[TagadaPay SDK] ⚠️  Internal path mismatch - not using meta tag config');
              }
            }
          } catch (e) {
            // Ignore parsing errors
            console.error('[TagadaPay SDK] ❌ Error parsing path remap meta tag:', e);
          }
        } else {
          console.log('[TagadaPay SDK] ❌ No tagadapay-path-remap meta tag found');
        }
      }

      // Extract params from current URL using external pattern
      if (externalPattern) {
        const externalMatch = matchesPathPattern(currentPath, externalPattern);
        if (externalMatch.matched) {
          // Map parameter names from external pattern to internal pattern
          // External: /off1/:lolo → {lolo: 'value'}
          // Internal: /post/:orderId → expects {orderId: 'value'}
          console.log('[TagadaPay SDK] 🔄 Mapping params from external to internal pattern');
          console.log('[TagadaPay SDK]    External params:', externalMatch.params);
          console.log('[TagadaPay SDK]    External pattern:', externalPattern);
          console.log('[TagadaPay SDK]    Internal pattern:', internalPath);

          const mappedParams = mapParamNames(
            externalMatch.params,
            externalPattern,
            internalPath
          );

          console.log('[TagadaPay SDK]    Mapped params:', mappedParams);
          return { matched: true, params: mappedParams };
        }
      }

      // Fallback: try to extract from current URL using internal pattern
      // (in case external pattern not found or doesn't match)
      const currentMatch = matchesPathPattern(currentPath, internalPath);
      return { matched: true, params: currentMatch.params };
    }

    // No remapping - match and extract from current path
    return matchesPathPattern(currentPath, internalPath);
  } catch (error) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[TagadaPay SDK] Error in matchRoute:', error);
    }
    return { matched: false, params: {} };
  }
}

/**
 * Check if the current URL should match a given internal path
 * 
 * Handles path remapping automatically. Use this for catch-all routing
 * implementations where you only need to check if a path matches.
 * 
 * For extracting parameters, use `matchRoute()` instead.
 * 
 * @param internalPath - The plugin's defined path (must start with /)
 * @returns true if current URL should render this path's component
 * @throws {Error} If internalPath is invalid
 * 
 * @example
 * ```typescript
 * // In a catch-all route handler
 * if (shouldMatchRoute('/hello')) {
 *   return <HelloPage />;
 * }
 * if (shouldMatchRoute('/about')) {
 *   return <AboutPage />;
 * }
 * ```
 */
export function shouldMatchRoute(internalPath: string): boolean {
  return matchRoute(internalPath).matched;
}

/**
 * Get the current matched path
 * 
 * Returns the actual path in the browser if it matches the given internal path.
 * Useful when you need the actual URL path for constructing links or API calls.
 * 
 * @param internalPath - The plugin's defined path
 * @returns The current browser path if it matches, null otherwise
 * 
 * @example
 * ```typescript
 * const matchedPath = getMatchedPath('/hello');
 * if (matchedPath) {
 *   console.log('Matched at:', matchedPath); // Could be /hello or /hello2
 * }
 * ```
 */
export function getMatchedPath(internalPath: string): string | null {
  // Validate input
  if (typeof internalPath !== 'string' || !internalPath.startsWith('/')) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn(`[TagadaPay SDK] Invalid internalPath: "${internalPath}"`);
    }
    return null;
  }

  // SSR check
  if (!isBrowser) {
    return null;
  }

  try {
    const currentPath = window.location.pathname;
    const remappedInternalPath = getInternalPath();

    // If current path matches internal path
    if (currentPath === internalPath ||
      (remappedInternalPath === internalPath && currentPath !== internalPath)) {
      return currentPath;
    }

    return null;
  } catch (error) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[TagadaPay SDK] Error in getMatchedPath:', error);
    }
    return null;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Manually clear the internal path cache
 * 
 * Useful for testing or when you know the meta tag has changed.
 * Normally you don't need to call this as it's handled automatically.
 * 
 * @example
 * ```typescript
 * // After dynamically updating the meta tag
 * document.querySelector('meta[name="x-plugin-internal-path"]')
 *   ?.setAttribute('content', '/new-path');
 * clearInternalPathCache();
 * ```
 */
export function clearInternalPathCache(): void {
  clearCache();
}

/**
 * Check if running in a browser environment
 * 
 * Useful for conditional logic in universal/isomorphic code.
 * 
 * @returns true if in browser, false if in SSR/Node environment
 * 
 * @example
 * ```typescript
 * if (isBrowserEnvironment()) {
 *   // Safe to use window/document
 *   const path = getInternalPath();
 * }
 * ```
 */
export function isBrowserEnvironment(): boolean {
  return isBrowser;
}

/**
 * Get debug information about the current routing state
 * 
 * Returns detailed information for debugging path remapping issues.
 * Only available in development mode.
 * 
 * @returns Debug information object
 * 
 * @example
 * ```typescript
 * const debug = getDebugInfo();
 * console.table(debug);
 * ```
 */
export function getDebugInfo(): Record<string, any> {
  if (!isBrowser) {
    return {
      environment: 'SSR/Node',
      error: 'Not in browser environment',
    };
  }

  try {
    const pathInfo = getPathInfo();
    const metaTag = document.querySelector('meta[name="x-plugin-internal-path"]');

    return {
      environment: 'Browser',
      externalPath: pathInfo.externalPath,
      internalPath: pathInfo.internalPath,
      isRemapped: pathInfo.isRemapped,
      fullUrl: pathInfo.fullUrl,
      query: Object.fromEntries(pathInfo.query.entries()),
      hash: pathInfo.hash,
      metaTagExists: !!metaTag,
      metaTagContent: metaTag?.getAttribute('content') || null,
      cacheHit: internalPathCache !== undefined,
      cachedValue: internalPathCache,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    return {
      environment: 'Browser',
      error: String(error),
    };
  }
}

// ============================================================================
// Cleanup
// ============================================================================

/**
 * Cleanup function to remove event listeners
 * 
 * Call this when unmounting the plugin or cleaning up resources.
 * Important for preventing memory leaks in SPAs.
 * 
 * @example
 * ```typescript
 * // In React
 * useEffect(() => {
 *   return () => {
 *     cleanupPathRemapping();
 *   };
 * }, []);
 * ```
 */
export function cleanupPathRemapping(): void {
  if (!isBrowser) return;

  try {
    window.removeEventListener('popstate', clearCache);
    window.removeEventListener('hashchange', clearCache);

    // Restore original history methods
    if (originalPushState) {
      history.pushState = originalPushState;
    }
    if (originalReplaceState) {
      history.replaceState = originalReplaceState;
    }

    clearCache();
  } catch (error) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[TagadaPay SDK] Error during cleanup:', error);
    }
  }
}

